import React, { useEffect, useState } from "react";
import styled, { withTheme } from "styled-components";
import {
  regex,
  emailRegex,
  authErrors,
  FieldsValidation,
} from "../../../modules/constants";
import { EmailInput } from "../../ui-library/AuthControls/EmailInput";
import { PasswordInput } from "../../ui-library/AuthControls/PasswordInput";
import { Button } from "../../ui-library/AuthControls/Button";
import TermsCheckbox from "../../ui-library/AuthControls/TermsCheckbox";
import Alert from "../../ui-library/Alert/Alert";
import AlertUGNX from "../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../ToastsAndAlerts/constants";
import { useTranslation } from "react-i18next";

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
`;

const Subtitle = styled.p`
  font-weight: 600;
  color: ${(props) => props.theme?.nero};
  opacity: 0.63;
`;

const Notice = styled.div`
  .title,
  .content {
    backface-visibility: hidden;
    transform: translateZ(0);
    transition: all 0.2s;
  }

  .title {
    color: ${(props) => props.theme?.fg};
    padding: 6px;
    border-bottom: 1px solid ${(props) => props.theme?.gray};
    display: block;
    background: ${(props) => props.theme?.whiteSmoke};

    i {
      position: absolute;
      right: 0;
      padding: 6px 12px;
    }
  }
  .title:after,
  .title:before {
    position: absolute;
    width: 2px;
    height: 0.75em;
    transition: all 0.2s;
  }
  .title:after {
    transform: rotate(90deg);
  }

  .content {
    max-height: 500px;
    overflow: hidden;
    p {
      margin: 0;
      font-size: 0.9em;
      line-height: 1.5;
      padding: 6px;
      white-space: pre-wrap;
      border-bottom: 1px solid ${(props) => props.theme?.gray};
    }
  }

  .toggle {
    visibility: hidden;
  }

  .toggle:checked {
    & + .title + .content {
      max-height: 0;
      border-bottom: 0;
    }
    & + .title:before {
      transform: rotate(90deg) !important;
    }
    & + .title i {
      transform: rotate(180deg) !important;
    }
  }
`;

const SignUp = (props) => {
  const { handleRegister, errorMessage, showSuccessModal, handleModalClose } =
    props;

  const { t } = useTranslation();

  const [username, setUsername] = useState("");
  const [confirmUsername, setConfirmUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(null);
  const [validUsername, setValidUsername] = useState(false);
  const [validPassword, setValidPassword] = useState(false);
  const [validConfirmPassword, setValidConfirmPassword] = useState(false);

  const onUsernameChange = (e) => {
    setUsername(e.target.value);
  };

  const onConfirmUsernameChange = (e) => {
    setConfirmUsername(e.target.value);
  };

  const onCheckboxChange = (value) => {
    setAcceptedTerms(value);
  };

  const onPasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const onConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleRegister(username, password, acceptedTerms);
  };

  const handleDone = (e) => {
    e.preventDefault();
    handleModalClose();
  };

  useEffect(() => {
    if (
      (username && !emailRegex.test(username)) ||
      username?.length > FieldsValidation.email.size ||
      username?.split("@")[0].length > FieldsValidation.email.localPart ||
      !username
    ) {
      setValidUsername(false);
    } else {
      setValidUsername(true);
    }
  }, [username]);

  useEffect(() => {
    if ((password && !regex.test(password)) || !password) {
      setValidPassword(false);
    } else {
      setValidPassword(true);
    }
  }, [password]);

  useEffect(() => {
    if ((confirmPassword && !regex.test(confirmPassword)) || !confirmPassword) {
      setValidConfirmPassword(false);
    } else {
      setValidConfirmPassword(true);
    }
  }, [confirmPassword]);

  return showSuccessModal ? (
    <div data-testid="success-modal" className="auth-form">
      <Alert
        callback={handleDone}
        message={`${t("appText.signup.confirmation.email")} ${username}. ${t(
          "appText.signup.confirmation.checkSpam"
        )}.`}
      />
    </div>
  ) : (
    <form
      onSubmit={handleSubmit}
      className="auth-form"
      data-testid="signup-view"
    >
      <Subtitle>{t("appText.signup.label.form")}.</Subtitle>
      <EmailInput
        onChange={onUsernameChange}
        value={username}
        inputError={username && !validUsername}
        field="1"
      />
      {username && (
        <StyledError>
          {!validUsername && (
            <span className="invalid-input">{authErrors.invalidEmail}</span>
          )}
        </StyledError>
      )}
      <EmailInput
        title={t("appText.signup.confirmEmail.input")}
        onChange={onConfirmUsernameChange}
        value={confirmUsername}
        inputError={confirmUsername && !validUsername}
        preventActions={true}
        margin="mt-3"
        field="2"
      />
      {username && (
        <StyledError>
          {username !== confirmUsername && validUsername && (
            <span>{authErrors.emailsNoMatch}</span>
          )}
        </StyledError>
      )}
      <PasswordInput
        placeholder={t("appText.signup.password.input")}
        onChange={onPasswordChange}
        value={password}
        inputError={password !== confirmPassword}
        margin="mb-3 mt-3"
        field="1"
      />
      <PasswordInput
        placeholder={t("appText.signup.confirmPassword.input")}
        onChange={onConfirmPasswordChange}
        value={confirmPassword}
        inputError={password !== confirmPassword}
        field="2"
      />
      {password && (
        <StyledError>
          {!validPassword && (
            <span className="invalid-input">{authErrors.invalidPassword}</span>
          )}
          {password !== confirmPassword && validPassword && (
            <span>{authErrors.noMatch}</span>
          )}
        </StyledError>
      )}
      <Notice>
        <input type="checkbox" id="notice-toggle" className="toggle" />
        <label className="title" htmlFor="notice-toggle">
          {t("appText.signup.notice.label")}
          <i className="fas fa-chevron-up" />
        </label>
        <div className="content">
          <p>
            {t("appText.signup.notice.paragraph.first")}{" "}
            <a
              href="https://www.ultragenyx.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="block__link small-link"
            >
              Ultragenyx.com
            </a>
            . {t("appText.signup.notice.paragraph.second")}
          </p>
        </div>
      </Notice>
      <TermsCheckbox
        policyHref="https://www.ultragenyx.com/privacy-policy/"
        onChange={onCheckboxChange}
      />
      {errorMessage && (
        <AlertUGNX type={AlertType.DANGER}>{errorMessage}</AlertUGNX>
      )}
      <Button
        text={t("appText.signup.button")}
        disabled={
          !(
            validPassword &&
            validConfirmPassword &&
            validUsername &&
            acceptedTerms &&
            password === confirmPassword &&
            username === confirmUsername
          )
        }
      />
    </form>
  );
};

export default withTheme(SignUp);
